import React, { useState, useCallback, useEffect } from "react";
import { Tabs, Typography, Button, Space, Tooltip } from "antd";
import { EditOutlined, EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { EditorContent } from "@tiptap/react";
import MenuBar from "./MenuBar/MenuBar";
import EmailPreview from "./EmailPreview";
import CollapsibleSidePanel from "./CollapsibleSidePanel";
import { prepareHtmlForPreview, renderContentBlocksToHtml } from "../utils/htmlCleanup";

const { Text } = Typography;

/**
 * 编辑器内容区域组件 - 包含编辑/预览标签页切换
 */
const EmailEditorContent = ({
    editor,
    toolbarButtons,
    showSidePanel,
    enableV2Mode,
    sidePanelSections,
    contentBlockInstances,
    onInstanceHover,
    onInsertBlock,
    onInsertInlineBlock,
    onRemoveBlock,
    onUpdateBlock,
    // 预览相关 props
    form,
    attachments = [],
    // 业务上下文用于内容块渲染
    businessContext = {},
}) => {
    const [activeTab, setActiveTab] = useState("edit");
    const [previewContent, setPreviewContent] = useState("");
    // 新增状态来跟踪预览数据的更新
    const [previewData, setPreviewData] = useState({
        subject: "",
        recipients: {},
        contentHtml: "",
        attachments: []
    });

    // 更新预览数据的函数
    const updatePreviewData = useCallback(() => {
        if (!editor || !form) return;
        
        const rawHtml = editor.getHTML();
        const renderedHtml = renderContentBlocksToHtml(rawHtml, businessContext, contentBlockInstances);
        
        // 实时获取表单数据
        const currentFormValues = form.getFieldsValue();
        
        setPreviewData({
            subject: currentFormValues.subject || "",
            recipients: {
                to: currentFormValues.to || "",
                cc: currentFormValues.cc || "",
                bcc: currentFormValues.bcc || "",
            },
            contentHtml: renderedHtml,
            attachments: attachments || []
        });
        
        setPreviewContent(rawHtml);
    }, [editor, form, attachments, businessContext, contentBlockInstances]);

    const handleTabChange = useCallback(
        (key) => {
            setActiveTab(key);
            // 当切换到预览标签页时，更新预览内容
            if (key === "preview" && editor) {
                updatePreviewData();
            }
        },
        [editor, updatePreviewData],
    );

    // 监听编辑器内容变化，实时更新预览
    useEffect(() => {
        if (!editor) return;

        const updatePreview = () => {
            if (activeTab === "preview") {
                updatePreviewData();
            }
        };

        // 监听编辑器更新事件
        editor.on("update", updatePreview);

        return () => {
            editor.off("update", updatePreview);
        };
    }, [editor, activeTab, updatePreviewData]);

    // 监听预览相关数据变化
    useEffect(() => {
        if (activeTab === "preview") {
            updatePreviewData();
        }
    }, [activeTab, attachments, businessContext, updatePreviewData]);

    // 监听表单字段变化 - 移除定时器，避免无限循环
    // useEffect(() => {
    //     if (!form || activeTab !== "preview") return;

    //     // 设置一个定时器来定期检查表单变化
    //     const interval = setInterval(() => {
    //         if (activeTab === "preview") {
    //             updatePreviewData();
    //         }
    //     }, 500);

    //     return () => {
    //         clearInterval(interval);
    //     };
    // }, [form, activeTab, updatePreviewData]);

    // 监听快捷键
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ctrl/Cmd + Shift + P 切换到预览模式
            if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "P") {
                event.preventDefault();
                const newTab = activeTab === "edit" ? "preview" : "edit";
                setActiveTab(newTab);
                // 立即更新预览数据
                if (newTab === "preview") {
                    updatePreviewData();
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [activeTab, updatePreviewData]);

    // 获取当前编辑器内容用于预览
    const getPreviewContent = useCallback(() => {
        return previewData.contentHtml || "";
    }, [previewData.contentHtml]);

    // 手动刷新预览内容
    const handleRefreshPreview = useCallback(() => {
        console.log("[EmailEditorContent] 手动刷新预览");
        updatePreviewData();
    }, [updatePreviewData]);

    // 标签页配置
    const tabItems = [
        {
            key: "edit",
            label: (
                <span>
                    <EditOutlined />
                    编辑
                </span>
            ),
            children: (
                <div className="edit-tab-content">
                    <MenuBar editor={editor} buttons={toolbarButtons} />
                    <div className="editor-with-sidebar">
                        <div className="editor-area">
                            <EditorContent editor={editor} />
                        </div>
                        {showSidePanel && enableV2Mode && (
                            <CollapsibleSidePanel
                                sections={sidePanelSections}
                                contentBlockInstances={contentBlockInstances}
                                onInstanceHover={onInstanceHover}
                                onInsertBlock={onInsertBlock}
                                onInsertInlineBlock={onInsertInlineBlock}
                                onRemoveBlock={onRemoveBlock}
                                onUpdateBlock={onUpdateBlock}
                            />
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: "preview",
            label: (
                <span>
                    <EyeOutlined />
                    预览
                </span>
            ),
            children: (
                <div className="preview-tab-content">
                    <div className="preview-toolbar">
                        <Space>
                            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                                预览会自动更新编辑内容
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: "11px", opacity: 0.7 }}>
                                快捷键: Ctrl+Shift+P 切换编辑/预览
                            </Typography.Text>
                        </Space>
                        <Tooltip title="手动刷新预览内容">
                            <Button type="text" size="small" icon={<ReloadOutlined />} onClick={handleRefreshPreview}>
                                刷新
                            </Button>
                        </Tooltip>
                    </div>
                    <EmailPreview 
                        subject={previewData.subject} 
                        recipients={previewData.recipients} 
                        contentHtml={getPreviewContent()} 
                        attachments={previewData.attachments} 
                    />
                </div>
            ),
        },
    ];

    return (
        <div className="email-editor-content">
            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={tabItems}
                className="email-editor-tabs"
                size="large"
                style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
                tabBarStyle={{
                    marginBottom: 0,
                    borderBottom: "1px solid #f0f0f0",
                    paddingLeft: "16px",
                }}
            />
        </div>
    );
};

export default EmailEditorContent;
